import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Fade,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PhotoAlbum as AlbumIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  getAlbumDetails,
  getAlbumImages,
  removeImageFromAlbum,
  updateAlbum,
  getThumbnailUrl,
  uploadImages,
  addImageToAlbum
} from '../services/api';

const MotionCard = motion(Card);

const AlbumView = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  const loadAlbum = async () => {
    try {
      const albumData = await getAlbumDetails(albumId);
      setAlbum(albumData);
    } catch (err) {
      console.error('Error loading album:', err);
      setError('Failed to load album details.');
    }
  };

  const loadImages = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      const response = await getAlbumImages(albumId, pageNum, 20);

      if (append) {
        setImages(prev => [...prev, ...response.images]);
      } else {
        setImages(response.images);
      }

      setTotalCount(response.total);
      setHasMore(response.images.length === 20);
      setError(null);
    } catch (err) {
      console.error('Error loading images:', err);
      setError('Failed to load album images.');
    } finally {
      setLoading(false);
    }
  }, [albumId]);

  useEffect(() => {
    loadAlbum();
    loadImages(1, false);
  }, [albumId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page > 1) {
      loadImages(page, true);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveImage = async (imageId, event) => {
    event.stopPropagation();

    if (window.confirm('Remove this image from the album?')) {
      try {
        await removeImageFromAlbum(albumId, imageId);
        setImages(prev => prev.filter(img => img.id !== imageId));
        setTotalCount(prev => prev - 1);

        // Update album's image count
        if (album) {
          setAlbum({ ...album, image_count: album.image_count - 1 });
        }
      } catch (err) {
        console.error('Error removing image:', err);
        setError('Failed to remove image from album.');
      }
    }
  };

  const handleSetCoverImage = async (imageId) => {
    try {
      await updateAlbum(albumId, { cover_image_id: imageId });
      // Reload album to get updated cover
      loadAlbum();
    } catch (err) {
      console.error('Error setting cover image:', err);
      setError('Failed to set cover image.');
    }
  };

  const handleImageClick = (imageId) => {
    navigate(`/image/${imageId}`);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  // Upload handlers
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadResults([]);
    setUploadProgress(0);

    try {
      // Step 1: Upload images with AI processing
      setUploadProgress(30);
      const uploadResponse = await uploadImages(acceptedFiles);
      setUploadProgress(60);

      // Step 2: Add each successfully uploaded image to the album
      const albumAddResults = [];
      let successCount = 0;

      for (const result of uploadResponse) {
        if (!result.error && result.id) {
          try {
            await addImageToAlbum(albumId, result.id);
            albumAddResults.push({ ...result, addedToAlbum: true });
            successCount++;
          } catch (err) {
            console.error('Error adding image to album:', err);
            albumAddResults.push({ ...result, addedToAlbum: false, albumError: err.message });
          }
        } else {
          albumAddResults.push(result);
        }
      }

      setUploadProgress(90);
      setUploadResults(albumAddResults);

      // Step 3: Refresh album to show new images
      if (successCount > 0) {
        await loadImages(1, false);
        await loadAlbum(); // Refresh album to update image count
      }

      setUploadProgress(100);

    } catch (err) {
      console.error('Upload error:', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      // Clear progress after a short delay
      setTimeout(() => setUploadProgress(0), 2000);
    }
  }, [albumId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp']
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024, // 50MB
    noClick: true, // Only allow drag & drop, not clicking
  });

  const clearUploadResults = () => {
    setUploadResults([]);
    setUploadError(null);
  };

  if (loading && images.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={60} sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  return (
    <Box {...getRootProps()} sx={{ position: 'relative', minHeight: '100vh' }}>
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '3px dashed #667eea',
          }}
        >
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'rgba(26, 26, 46, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '2px dashed #667eea',
              borderRadius: 3,
            }}
          >
            <UploadIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
            <Typography variant="h5" sx={{ color: '#667eea', mb: 1 }}>
              Drop images to add to this album
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Images will be processed with AI and automatically added to "{album?.name}"
            </Typography>
          </Paper>
        </Box>
      )}

      <Box>
      {/* Header with breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/albums')}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textDecoration: 'none',
              '&:hover': {
                color: '#ffffff',
                textDecoration: 'underline'
              }
            }}
          >
            Albums
          </Link>
          <Typography sx={{ color: '#ffffff' }}>
            {album?.name || 'Loading...'}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 1,
                color: '#ffffff',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AlbumIcon /> {album?.name}
            </Typography>
            {album?.description && (
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  mb: 2,
                  maxWidth: 600
                }}
              >
                {album.description}
              </Typography>
            )}
            <Chip
              label={`${totalCount} ${totalCount === 1 ? 'image' : 'images'}`}
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.2)',
                color: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
              }}
            />
          </Box>

          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/albums')}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            Back to Albums
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Upload Progress */}
      {uploading && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: 'rgba(26, 26, 46, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
            Uploading to Album
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              mb: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {uploadProgress < 30 && 'Uploading images...'}
            {uploadProgress >= 30 && uploadProgress < 60 && 'AI analyzing images...'}
            {uploadProgress >= 60 && uploadProgress < 90 && 'Adding to album...'}
            {uploadProgress >= 90 && 'Finishing up...'}
          </Typography>
        </Paper>
      )}

      {/* Upload Error */}
      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 3 }}>
          {uploadError}
        </Alert>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: 'rgba(26, 26, 46, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Upload Results
            </Typography>
            <Button onClick={clearUploadResults} size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Clear
            </Button>
          </Box>

          <Grid container spacing={2}>
            {uploadResults.map((result, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    background: result.error || result.albumError
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(76, 175, 80, 0.1)',
                    border: `1px solid ${result.error || result.albumError
                      ? 'rgba(244, 67, 54, 0.3)'
                      : 'rgba(76, 175, 80, 0.3)'
                    }`,
                    p: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                    {result.filename || `Image ${index + 1}`}
                  </Typography>

                  {result.error ? (
                    <Typography variant="caption" sx={{ color: '#f44336' }}>
                      Upload failed: {result.error}
                    </Typography>
                  ) : result.albumError ? (
                    <Typography variant="caption" sx={{ color: '#f44336' }}>
                      Uploaded but failed to add to album: {result.albumError}
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#4caf50' }}>
                        ✅ Successfully added to album
                      </Typography>
                      {result.metadata?.description && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mt: 0.5 }}>
                          AI: {result.metadata.description}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {images.length === 0 ? (
        <Card
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: '#161616',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <AlbumIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            Album is Empty
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Add images from your gallery to this album
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              mt: 3,
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#764ba2',
              },
            }}
          >
            Go to Gallery
          </Button>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={image.id}>
                <Fade in timeout={300 + index * 50}>
                  <MotionCard
                    whileHover={{
                      scale: 1.03,
                      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                    }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      cursor: 'pointer',
                      background: 'rgba(26, 26, 46, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => handleImageClick(image.id)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="160"
                        image={getThumbnailUrl(image.thumbnail_path, image.thumbnail_s3_url, image.id) || '/placeholder-image.jpg'}
                        alt={image.description || 'Image'}
                        sx={{
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      />

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          gap: 1,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          '.MuiCard-root:hover &': {
                            opacity: 1,
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 126, 234, 0.8)',
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetCoverImage(image.id);
                          }}
                          title="Set as album cover"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.8)',
                            }
                          }}
                          onClick={(e) => handleRemoveImage(image.id, e)}
                          title="Remove from album"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Show if this is the current cover */}
                      {album?.cover_image_id === image.id && (
                        <Chip
                          label="Cover"
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            backgroundColor: 'rgba(102, 126, 234, 0.9)',
                            color: 'white',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {image.description || 'No description available'}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <InfoIcon fontSize="small" />
                        {image.width}×{image.height}
                        {image.face_count > 0 && ` • ${image.face_count} face${image.face_count > 1 ? 's' : ''}`}
                      </Typography>
                    </Box>
                  </MotionCard>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {images.length > 0 && hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Card
                  onClick={loadMore}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    background: 'rgba(26, 26, 46, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center',
                    minWidth: 200,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#667eea' }} />
                  ) : (
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Load More Images
                    </Typography>
                  )}
                </Card>
              </motion.div>
            </Box>
          )}
        </>
      )}
      </Box>
    </Box>
  );
};

export default AlbumView;